import React, { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  redirectPath?: string;
  redirectState?: Record<string, unknown>;
}

const LoginPromptModal = memo(({ isOpen, onClose, redirectPath, redirectState }: LoginPromptModalProps) => {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      await signInWithGoogle();
      onClose();
      // After successful Google sign-in, user will be redirected by OAuth flow
    } catch (error) {
      console.error("Google sign-in error:", error);
      toast.error("Error signing in with Google. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = () => {
    onClose();
    // Navigate to auth page with redirect info
    navigate("/auth", {
      state: {
        returnTo: redirectPath,
        returnState: redirectState,
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="glass-premium border-premium max-w-sm mx-auto p-0 overflow-hidden"
        hideCloseButton
        aria-describedby="login-prompt-description"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-background/50 hover:bg-background/80 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4 text-foreground/70" />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="p-6 pt-8 space-y-6"
            >
              {/* Logo */}
              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <img
                  src="https://assets.loverslabs.art/logoheroBSwh3kw.png"
                  alt="Logo"
                  className="h-12 md:h-16 w-auto"
                />
              </motion.div>

              {/* Title */}
              <motion.div
                className="text-center space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
              >
                <h2
                  className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent"
                  style={{
                    backgroundImage: "var(--gradient-premium)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                  }}
                >
                  Start Creating
                </h2>
                <p id="login-prompt-description" className="text-foreground/70 text-sm md:text-base">
                  Sign in with Google — start creating for free.
                </p>
              </motion.div>

              {/* Buttons */}
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {/* Google Button */}
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full gradient-premium hover:opacity-90 text-white border-0 transition-all shadow-lg hover:shadow-xl"
                  size="lg"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLoading ? "Signing in..." : "Continue with Google"}
                </Button>

                {/* Apple Button */}
                <Button
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await signInWithApple();
                      onClose();
                    } catch (error) {
                      console.error("Apple sign-in error:", error);
                      toast.error("Error signing in with Apple. Please try again.");
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className="w-full bg-white hover:bg-white/90 text-black border-0 transition-all shadow-lg hover:shadow-xl"
                  size="lg"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                    />
                  </svg>
                  {isLoading ? "Signing in..." : "Continue with Apple"}
                </Button>

                {/* Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {/* Email Button */}
                <Button
                  onClick={handleEmailSignIn}
                  variant="outline"
                  className="w-full glass-premium border-premium hover:bg-background/20"
                  size="lg"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Continue with Email
                </Button>
              </motion.div>

              {/* Legal text */}
              <motion.p
                className="text-xs text-center text-foreground/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                By continuing, you confirm that you are of legal age in your jurisdiction and agree to our{" "}
                <a href="/termsofservice" className="underline hover:text-gold transition-colors">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacypolicy" className="underline hover:text-gold transition-colors">
                  Privacy Policy
                </a>
                .
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
});

LoginPromptModal.displayName = "LoginPromptModal";

export default LoginPromptModal;
